/**
 * @file poSupplierRef.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-02
 * @modified 2026-11-02
 * @description "Add Supplier Ref & Confirm" on Search Purchase Orders (Cam,
 *              2 Nov 2026): the supplier's reference for a PO, and its status
 *              set to Confirmed in the same step — what the PO editor asks
 *              ("Mark as confirmed?") when a supplier reference is added.
 *
 *   The one rule for the row menus, the window and the save
 *   (services/poSupplierRefService). Written once here; utils/poSupplierRef.js
 *   on the server is GENERATED from it by scripts/sync-shared-utils.js.
 *
 *   - Not offered (greyed with the reason): an archived or voided PO (a
 *     reference is still kept on it in Edit, where it stays archived), a PO-H
 *     (counted as confirmed once sent — hauliers give no reference), a PO
 *     made in Xero while the Xero PO sync is on (cost only here).
 *   - The reference is required (it is what the action adds), at most 255
 *     characters, spaces tidied.
 *   - Confirmed: offered from Draft, Authorised, Sent (and Xero's Submitted
 *     before delivery). Already Confirmed: nothing to move. Billed: the status
 *     comes from its bill, so only the reference is saved. Its order delivered
 *     or invoiced: forward only (utils/poStatusMove) — Confirmed is the last
 *     step, so it is a forward move from any of them. Moving to Confirmed
 *     marks it sent to the supplier, as the PO save and bulk status do.
 *     Nothing is emailed to anyone.
 */

import { normaliseStatusForMove, statusMoveAfterDelivery, statusMoveLabel } from './poStatusMove';

/** The action's name, on the menus, the window and the history. */
export const SUPPLIER_REF_ACTION = 'Add Supplier Ref & Confirm';

/** The column's length (purchasing.purchase_orders.supplier_reference varchar(255)). */
export const SUPPLIER_REF_MAX = 255;

/** A reference as kept: spaces tidied, never null. */
export function cleanSupplierRef(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

const ARCHIVED = new Set(['DELETED', 'VOIDED']);

/**
 * Why the window cannot be used on this PO at all, or null when it can.
 *
 * @param {object} po
 * @param {string} [po.status]
 * @param {string} [po.poType] — 'haulage' for a PO-H
 * @param {boolean} [po.xeroCostOnly] — made in Xero while the Xero PO sync is on
 * @param {boolean} [po.saved] — false when it is not saved here (no local id)
 * @returns {string|null}
 */
export function supplierRefBlock({ status, poType, xeroCostOnly = false, saved = true } = {}) {
  if (saved === false) return 'It is not saved here yet';
  const s = normaliseStatusForMove(status);
  if (ARCHIVED.has(s)) {
    return `It is ${s === 'VOIDED' ? 'voided' : 'archived'} — a supplier reference can still be kept on it in Edit, where it stays ${s === 'VOIDED' ? 'voided' : 'archived'}`;
  }
  if (poType === 'haulage') return 'A PO-H counts as confirmed once it is sent — hauliers give no confirmation reference';
  if (xeroCostOnly) return 'It was made in Xero and the Xero PO sync is on, so only its cost price can be changed here';
  return null;
}

/**
 * Whether the save may set the PO Confirmed: `{ already }` when it is
 * Confirmed now, `{ block }` (the reason) when it may not be, else `{}`.
 *
 * @param {object} args
 * @param {string} args.status — as stored
 * @param {boolean} [args.delivered] — its customer order is delivered or invoiced
 * @returns {{ already?: boolean, block?: string }}
 */
export function confirmState({ status, delivered = false } = {}) {
  const s = normaliseStatusForMove(status);
  if (s === 'CONFIRMED') return { already: true };
  if (s === 'BILLED') return { block: 'It is billed — its status comes from its bill, so it stays Billed. Only the reference is saved.' };
  if (ARCHIVED.has(s)) return { block: 'It is archived' };
  if (delivered) {
    const why = statusMoveAfterDelivery(s, 'CONFIRMED');
    if (why) return { block: `Its order is delivered — ${why}` };
  }
  return {};
}

/** The menu item's hover words. */
export function supplierRefMenuTitle(po) {
  const block = supplierRefBlock(po);
  if (block) return `Cannot — ${block}`;
  const s = normaliseStatusForMove(po?.status);
  const ref = cleanSupplierRef(po?.supplierReference);
  if (s === 'CONFIRMED') return ref ? `Confirmed — change the supplier's reference (${ref})` : "Confirmed — add the supplier's reference";
  if (s === 'BILLED') return "Add the supplier's reference — it is billed, so its status stays Billed";
  return `Add the supplier's reference and set it Confirmed (it is ${statusMoveLabel(s)} now)`;
}

/**
 * What a save would do, or why it may not.
 *
 * @param {object} args
 * @param {{ supplierReference?: string|null, status?: string }} args.stored
 * @param {{ supplierReference?: string|null, confirm?: boolean }} args.asked
 * @param {boolean} [args.delivered]
 * @returns {{ error?: string, code?: string, ref?: string, refChanged?: boolean,
 *             statusFrom?: string, statusTo?: string, moving?: boolean, unchanged?: boolean }}
 */
export function planSupplierRef({ stored = {}, asked = {}, delivered = false } = {}) {
  const ref = cleanSupplierRef(asked.supplierReference);
  if (!ref) return { error: "Enter the supplier's reference.", code: 'SUPPLIER_REF_REQUIRED' };
  if (ref.length > SUPPLIER_REF_MAX) {
    return { error: `The supplier's reference is ${ref.length} characters — at most ${SUPPLIER_REF_MAX}.`, code: 'SUPPLIER_REF_TOO_LONG' };
  }
  const statusFrom = normaliseStatusForMove(stored.status);
  const state = confirmState({ status: statusFrom, delivered });
  let statusTo = statusFrom;
  if (asked.confirm && !state.already) {
    if (state.block) return { error: `It can't be set Confirmed: ${state.block}`, code: 'CONFIRM_REFUSED' };
    statusTo = 'CONFIRMED';
  }
  const refChanged = ref !== cleanSupplierRef(stored.supplierReference);
  const moving = statusTo !== statusFrom;
  return { ref, refChanged, statusFrom, statusTo, moving, unchanged: !refChanged && !moving };
}

/** What a save did, in a sentence. */
export function savedMessage({ number, ref, refChanged, moving, statusFrom }) {
  const what = number || 'The PO';
  if (refChanged && moving) return `${what}: supplier reference ${ref} saved, and set Confirmed (was ${statusMoveLabel(statusFrom)}).`;
  if (moving) return `${what} set Confirmed (was ${statusMoveLabel(statusFrom)}) — its supplier reference ${ref} was already saved.`;
  if (refChanged) return `${what}: supplier reference ${ref} saved.`;
  return `${what}: nothing changed — it already had that reference.`;
}
